/**
 * Settings — Import Annual Reports.
 *
 * Choose the printed report. The page reads its page count, draws the front cover, and
 * shelves it.
 *
 * WHAT THIS PAGE NO LONGER DOES. It used to build a second edition of every report for
 * view-only accounts and donors — every photograph pixelated, and later every face found
 * and covered with a black box. Both are gone. One file is stored, exactly as it was
 * handed over, and covering the faces happens before that, in the PDF, by a person.
 *
 * WHICH MAKES THE TICK-BOX THE WHOLE CONTROL, and worth understanding before moving it.
 * A report is only readable by supporters if somebody states that its faces are already
 * covered; that statement is the single thing standing between a photograph of a child
 * and every donor with a password. Nothing here can check it — this page can render a
 * cover, and neither it nor the Worker can look at a photograph and tell you whether the
 * people in it are recognisable. So it is deliberately an action a person takes and not a
 * default: unticked shelves the report for staff only, which is safe and useless, and
 * that is the right way round for a box somebody might tick without reading.
 *
 * The asking is done twice, in the two places somebody is actually looking: inside the
 * dropzone, because what it asks for is a property the file must already have before it
 * is chosen, and beside the Shelve button, which is the last moment anything can change.
 *
 * Reports shelved before all this keep the separately prepared edition they came with;
 * the Worker still serves it, and the tick-box has no bearing on them.
 */
import { useState } from 'react'
import { PageHeader } from '../App'
import { useApi, api } from '../lib/api'
import { readShelfPdf, yearOf } from '../lib/reportPdf'
import {
  Card, Badge, Notice, Empty, Loading, ErrorState, ConfirmModal,
  useToast, fmtBytes,
} from '../components/ui'
import { Dropzone } from '../components/Dropzone'
import { FacesWarning, WhoCanRead } from '../components/ShelfImport'

const phaseLabel = (p) => {
  if (p?.phase === 'cover') return 'Drawing the cover…'
  return 'Reading the file…'
}

/** One report that has been read and is ready to shelve. */
function PreparedReport({ report }) {
  return (
    <Card
      title={`${report.year} — ${report.title}`}
      sub={`${report.page_count} pages · ${fmtBytes(report.bytes)} · ${report.source}`}
      actions={<Badge tone="ok">Ready</Badge>}
    >
      <div className="row" style={{ alignItems: 'flex-start', gap: 16 }}>
        <img
          src={report.coverUrl}
          alt={`Front cover of the ${report.year} report`}
          style={{ width: 180, borderRadius: 6, border: '1px solid var(--border)' }}
        />
        <div className="small subtle">
          The file will be shelved exactly as it stands. Check this is the right one and
          the right year — the year is read from the filename, and it is what the shelf is
          ordered and addressed by. This is the printed cover; if the faces on it are not
          covered, the file is not ready.
        </div>
      </div>
    </Card>
  )
}

export default function SettingsAnnualReports({ user }) {
  const shelf = useApi('annual-reports', 'annual-reports')
  const toast = useToast()
  const [prepared, setPrepared] = useState([])
  const [working, setWorking] = useState(null)
  const [progress, setProgress] = useState(null)
  const [busy, setBusy] = useState(false)
  const [removing, setRemoving] = useState(null)
  // Never remembered between batches. Whether the faces are covered is a fact about the
  // files in front of you, and carrying a tick over from the last year shelved would be
  // the application answering the one question it exists to ask.
  const [covered, setCovered] = useState(false)

  const mayImport = user?.caps?.manageSettings && user?.caps?.uploadMedia
  if (!mayImport) {
    return (
      <PageHeader title="Import Annual Reports">
        <Notice tone="warn" icon="lock" title="Not available to your role">
          Shelving a report writes a file to the archive, so it is kept to owners and
          administrators. You are signed in as <strong>{user?.roleLabel}</strong>.
        </Notice>
      </PageHeader>
    )
  }

  const choose = async (fileList) => {
    const pdfs = Array.from(fileList ?? []).filter((f) => /\.pdf$/i.test(f.name))
    if (!pdfs.length) {
      toast.error('Choose one or more annual report PDFs.')
      return
    }
    const out = []
    for (const file of pdfs) {
      const year = yearOf(file.name)
      if (!year) {
        toast.error(`${file.name}: no year in the filename, so it was skipped.`)
        continue
      }
      setWorking({ name: file.name, year })
      setProgress(null)
      try {
        // The year is attached here rather than inside the reader: what addresses a
        // shelved document is the shelf's business, and the newsletters address theirs
        // by a date. `rightHalf` because these reports are imposed for print — see
        // readShelfPdf.
        out.push({
          year,
          ...(await readShelfPdf(file, {
            title: `${year} Annual Report`,
            rightHalf: true,
            onProgress: setProgress,
          })),
        })
      } catch (err) {
        toast.error(`${file.name}: ${err.message || 'could not be read'}`)
      }
    }
    setWorking(null)
    setProgress(null)
    setPrepared(out)
  }

  const shelveAll = async () => {
    setBusy(true)
    let done = 0
    for (const r of prepared) {
      const fd = new FormData()
      fd.set('year', String(r.year))
      fd.set('title', r.title)
      fd.set('page_count', String(r.page_count))
      fd.set('file', r.files.file, `${r.year} Annual Report.pdf`)
      fd.set('cover', r.files.cover, `${r.year}.jpg`)
      // What makes the Worker store the file under both editions. Sent only when it is
      // true, so an unticked box is an absent claim rather than a false one.
      if (covered) fd.set('faces_covered', '1')
      try {
        await api('annual-reports', { method: 'POST', formData: fd })
        done++
      } catch (err) {
        toast.error(`${r.year}: ${err.message || 'could not be shelved'}`)
      }
    }
    setBusy(false)
    for (const r of prepared) URL.revokeObjectURL(r.coverUrl)
    setPrepared([])
    setCovered(false)
    await shelf.refetch()
    if (done) {
      toast.ok(
        `Shelved ${done} report${done === 1 ? '' : 's'}` +
          (covered ? ', readable by supporters.' : ' for staff only.')
      )
    }
  }

  const remove = async (row) => {
    try {
      await api(`annual-reports/${row.id}`, { method: 'DELETE' })
      toast.ok(`Removed the ${row.year} report.`)
      await shelf.refetch()
    } catch (err) {
      toast.error(err)
    } finally {
      setRemoving(null)
    }
  }

  const startAgain = () => {
    for (const r of prepared) URL.revokeObjectURL(r.coverUrl)
    setPrepared([])
    setCovered(false)
  }

  return (
    <PageHeader
      title="Import Annual Reports"
      sub="The reports on the shelf, and how to add a year"
      actions={
        prepared.length ? (
          <button className="btn" onClick={startAgain} disabled={busy}>
            Start again
          </button>
        ) : null
      }
    >
      <div className="col">
        {!prepared.length && !working && (
          <Card
            title="Add a Year"
            sub="Cover the faces in the PDF, choose it here, and it is shelved as it stands."
          >
            <Notice tone="info" icon="book" title="How a year gets onto the shelf">
              <ol className="small" style={{ margin: 0, paddingLeft: '1.2em' }}>
                <li>
                  Open the printed report in whatever you use to edit a PDF and blur or
                  black out every face in it. Nothing in this application does this for
                  you, and nothing here can check that it was done.
                </li>
                <li>
                  Save it with the year in the filename — that is where the year is read
                  from — and choose it below.
                </li>
                <li>
                  Tick to say the faces are covered. That tick is what lets a view-only
                  account and a donor read the report; without it the year is shelved for
                  staff only and supporters are told it is unavailable.
                </li>
              </ol>
            </Notice>

            <Dropzone
              onFiles={choose}
              accept="application/pdf"
              multiple
              icon="book"
              title="Choose the annual report PDFs"
              warn={<FacesWarning />}
            >
              Drop them here, or click to pick. The year is read from the filename, and the
              front cover is drawn from the first page.
            </Dropzone>
            <p className="small subtle" style={{ margin: 0 }}>
              Nothing leaves this browser until you press Shelve.
            </p>
          </Card>
        )}

        {working && (
          <Card title={`Reading ${working.year}`} sub={working.name}>
            <div className="row">
              <Loading label={phaseLabel(progress)} rows={1} />
            </div>
          </Card>
        )}

        {prepared.map((r) => (
          <PreparedReport key={r.year} report={r} />
        ))}

        {prepared.length > 0 && (
          <Card title="Shelve">
            <WhoCanRead
              covered={covered}
              onCovered={setCovered}
              count={prepared.length}
              item="report"
              kind="report"
            />
            <div className="row">
              <button className="btn btn-primary" onClick={shelveAll} disabled={busy}>
                {busy
                  ? 'Shelving…'
                  : `Shelve ${prepared.length} report${prepared.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </Card>
        )}

        <Card title="On the Shelf" sub="Newest first">
          {shelf.isLoading ? (
            <Loading rows={3} />
          ) : shelf.isError ? (
            <ErrorState error={shelf.error} retry={shelf.refetch} />
          ) : !shelf.data?.rows?.length ? (
            <Empty icon="book" title="No reports yet" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Title</th>
                    <th>Pages</th>
                    <th>Size</th>
                    <th>Supporters can open it</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shelf.data.rows.map((r) => (
                    <tr key={r.id}>
                      <td className="mono nowrap">{r.year}</td>
                      <td>{r.title}</td>
                      <td className="nowrap">{r.page_count ? `${r.page_count} pp` : '—'}</td>
                      <td className="nowrap">{r.bytes ? fmtBytes(r.bytes) : '—'}</td>
                      <td>
                        {/* Rows shelved before the second edition was dropped still have
                            one, and still serve it. The column is the only place that
                            difference is visible, so it stays. */}
                        {r.has_anon_edition ? (
                          <Badge tone="ok">Yes</Badge>
                        ) : (
                          <Badge tone="warn">No — not shown to them</Badge>
                        )}
                      </td>
                      <td className="nowrap">
                        <button className="btn btn-sm" onClick={() => setRemoving(r)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <ConfirmModal
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => remove(removing)}
        title={`Remove the ${removing?.year ?? ''} report?`}
        body="The file and its cover are deleted from storage. The report can be shelved again at any time."
        confirmLabel="Remove"
      />
    </PageHeader>
  )
}
